import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RfpAddQuestionDrawer } from "../components/organisms/RfpAddQuestionDrawer";
import {
  RfpAssignQuestionsDrawer,
  type AssignQuestionOption,
  type AssignSectionOption,
} from "../components/organisms/RfpAssignQuestionsDrawer";
import { RfpDiscussionDrawer } from "../components/organisms/RfpDiscussionDrawer";
import { RfpFindAnswerDrawer } from "../components/organisms/RfpFindAnswerDrawer";
import { RfpQuestionAnswerCard } from "../components/organisms/RfpQuestionAnswerCard";
import type {
  RfpQuestionDeselectAnswerContext,
  RfpQuestionDiscussionContext,
  RfpQuestionFindAnswerContext,
  RfpQuestionLockContext,
  RfpQuestionSelectAnswerContext,
  RfpQuestionUpdateAnswerContext,
} from "../components/organisms/RfpQuestionAnswerCard";
import { RfpQuestionAnswerSectionSidebar } from "../components/organisms/RfpQuestionAnswerSectionSidebar";
import { RfpQuestionAnswersFooter } from "../components/organisms/RfpQuestionAnswersFooter";
import { RfpQuestionAnswersHeader } from "../components/organisms/RfpQuestionAnswersHeader";
import { useAuthSession } from "../contexts/AuthSessionContext";
import { useRfpFull } from "../hooks/useRfpFull";
import { useRfpUsers } from "../hooks/useRfpUsers";
import {
  addRfpQuestionToKb,
  bulkAssignRfpQuestions,
  createRfpQuestion,
  deselectRfpQuestionAnswer,
  exportRfpFile,
  heartbeatRfpPresence,
  lockRfpQuestion,
  selectRfpQuestionAnswer,
  syncRfpPendingQuestions,
  unlockRfpQuestion,
  updateRfpQuestionAnswer,
} from "../services/rfpsService";
import type {
  BulkAssignRfpQuestionsRequest,
  RfpAssignableUser,
  RfpFullSection,
  RfpPresenceActiveUser,
  RfpPresenceHeartbeatResponse,
  RfpUser,
} from "../types/rfp";
import {
  buildSidebarSections,
  computeAssignmentProgress,
  countSyncEligibleQuestions,
  filterRfpQuestionsByAssignee,
  filterRfpQuestionsBySearch,
  findFirstSectionWithAssignments,
} from "../utils/rfpQuestionFilters";

const QUESTIONS_PER_PAGE = 5;
const PRESENCE_HEARTBEAT_INTERVAL_MS = 15000;

const sortRfpSections = (sections: readonly RfpFullSection[]): RfpFullSection[] =>
  [...sections].sort(
    (a, b) =>
      (a.section_sort_order ?? 0) - (b.section_sort_order ?? 0) || a.name.localeCompare(b.name),
  );

const formatRfpUserDisplay = (user: RfpUser | null | undefined): string => {
  if (!user) return "—";
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  return user.name?.trim() || fullName || user.email?.trim() || "—";
};

export const RFPQuestionAnswersPage = () => {
  const navigate = useNavigate();
  const { rfpId } = useParams();
  const normalizedRfpId = rfpId?.trim() || null;
  const { user } = useAuthSession();
  const { rfp, loading, error, reload } = useRfpFull(normalizedRfpId);
  const refreshRfp = useCallback(() => reload({ silent: true }), [reload]);
  const { users: rfpUsers } = useRfpUsers();
  const currentUserId = user?.id?.trim() || null;
  const [activeSectionName, setActiveSectionName] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [assignedToMeOnly, setAssignedToMeOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [isDiscussionOpen, setIsDiscussionOpen] = useState(false);
  const [isAssignDrawerOpen, setIsAssignDrawerOpen] = useState(false);
  const [isFindAnswerDrawerOpen, setIsFindAnswerDrawerOpen] = useState(false);
  const [discussionContext, setDiscussionContext] = useState<RfpQuestionDiscussionContext | null>(
    null,
  );
  const [findAnswerContext, setFindAnswerContext] = useState<RfpQuestionFindAnswerContext | null>(
    null,
  );
  const [selectError, setSelectError] = useState<string | null>(null);
  const [selectingQuestionId, setSelectingQuestionId] = useState<string | null>(null);
  const [deselectError, setDeselectError] = useState<string | null>(null);
  const [deselectingQuestionId, setDeselectingQuestionId] = useState<string | null>(null);
  const [lockError, setLockError] = useState<string | null>(null);
  const [lockingQuestionId, setLockingQuestionId] = useState<string | null>(null);
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [unlockingQuestionId, setUnlockingQuestionId] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updatingQuestionId, setUpdatingQuestionId] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [activeUsers, setActiveUsers] = useState<readonly RfpPresenceActiveUser[]>([]);
  const [isAddQuestionOpen, setIsAddQuestionOpen] = useState(false);
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  const [addQuestionError, setAddQuestionError] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [presenceSyncRunning, setPresenceSyncRunning] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const prevPresenceSyncRunningRef = useRef(false);

  const orderedSections = useMemo(() => sortRfpSections(rfp?.sections ?? []), [rfp?.sections]);

  const assignedToMeCount = useMemo(() => {
    if (!currentUserId) return 0;
    return orderedSections.reduce(
      (count, section) =>
        count + filterRfpQuestionsByAssignee(section.questions, currentUserId).length,
      0,
    );
  }, [currentUserId, orderedSections]);

  const sidebarSections = useMemo(
    () =>
      buildSidebarSections(orderedSections, {
        assignedToMeOnly,
        currentUserId,
      }),
    [assignedToMeOnly, currentUserId, orderedSections],
  );

  const myAssignmentProgress = useMemo(
    () => computeAssignmentProgress(orderedSections, currentUserId),
    [currentUserId, orderedSections],
  );

  const syncEligibleCount = useMemo(
    () => countSyncEligibleQuestions(orderedSections),
    [orderedSections],
  );

  const isSyncRunning = isSyncing || presenceSyncRunning;

  const applyHeartbeatResponse = useCallback(
    (response: RfpPresenceHeartbeatResponse) => {
      setActiveUsers(response.active_users ?? []);

      const syncRunning = response.sync_pipeline_running === true;
      const wasRunning = prevPresenceSyncRunningRef.current;

      if (wasRunning && !syncRunning) {
        refreshRfp();
        setIsSyncing(false);
      }

      prevPresenceSyncRunningRef.current = syncRunning;
      setPresenceSyncRunning(syncRunning);
    },
    [refreshRfp],
  );

  const sectionNameOptions = useMemo(
    () => orderedSections.map((section) => section.name),
    [orderedSections],
  );

  const defaultAddQuestionSection = activeSectionName ?? orderedSections[0]?.name ?? "General";

  useEffect(() => {
    if (!orderedSections.length) {
      setActiveSectionName(null);
      return;
    }

    const navigableSections = assignedToMeOnly
      ? sidebarSections
      : orderedSections.map((section) => ({ name: section.name }));

    if (navigableSections.length === 0) {
      setActiveSectionName(null);
      return;
    }

    setActiveSectionName((current) => {
      if (current && navigableSections.some((section) => section.name === current)) {
        return current;
      }
      if (assignedToMeOnly) {
        return findFirstSectionWithAssignments(orderedSections, currentUserId);
      }
      return orderedSections[0]?.name ?? null;
    });
  }, [assignedToMeOnly, currentUserId, orderedSections, sidebarSections]);

  const activeSection = useMemo(
    () => orderedSections.find((section) => section.name === activeSectionName) ?? null,
    [activeSectionName, orderedSections],
  );

  const filteredQuestions = useMemo(() => {
    if (!activeSection) return [];
    let questions = [...activeSection.questions];
    if (assignedToMeOnly) {
      questions = filterRfpQuestionsByAssignee(questions, currentUserId);
    }
    return filterRfpQuestionsBySearch(questions, searchInput);
  }, [activeSection, assignedToMeOnly, currentUserId, searchInput]);

  const totalPages = Math.max(1, Math.ceil(filteredQuestions.length / QUESTIONS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * QUESTIONS_PER_PAGE;
  const pagedQuestions = filteredQuestions.slice(pageStart, pageStart + QUESTIONS_PER_PAGE);

  useEffect(() => {
    setPage(1);
  }, [assignedToMeOnly, searchInput]);

  const handleSectionChange = useCallback((name: string) => {
    setActiveSectionName(name);
    setPage(1);
  }, []);

  const handleOpenDiscussion = useCallback((context: RfpQuestionDiscussionContext) => {
    setDiscussionContext(context);
    setIsDiscussionOpen(true);
  }, []);

  const handleCloseDiscussion = useCallback(() => {
    setIsDiscussionOpen(false);
    refreshRfp();
  }, [refreshRfp]);
  const handleOpenAssignDrawer = useCallback(() => {
    setAssignError(null);
    setIsAssignDrawerOpen(true);
  }, []);
  const handleOpenAddQuestionDrawer = useCallback(() => {
    setAddQuestionError(null);
    setIsAddQuestionOpen(true);
  }, []);
  const handleCloseAddQuestionDrawer = useCallback(() => {
    if (isAddingQuestion) {
      return;
    }
    setIsAddQuestionOpen(false);
  }, [isAddingQuestion]);
  const handleAddQuestion = useCallback(
    async ({ questionText, section }: { questionText: string; section: string }): Promise<boolean> => {
      if (!normalizedRfpId || isAddingQuestion) {
        return false;
      }

      setAddQuestionError(null);
      setIsAddingQuestion(true);
      try {
        await createRfpQuestion(normalizedRfpId, {
          question_text: questionText,
          section,
        });
        refreshRfp();
        return true;
      } catch (e) {
        const message = e instanceof Error ? e.message : "Failed to add question";
        setAddQuestionError(message);
        return false;
      } finally {
        setIsAddingQuestion(false);
      }
    },
    [isAddingQuestion, normalizedRfpId, refreshRfp],
  );
  const handleSyncPending = useCallback(async () => {
    if (!normalizedRfpId || isSyncRunning || syncEligibleCount === 0) {
      return;
    }

    setSyncError(null);
    setIsSyncing(true);
    try {
      await syncRfpPendingQuestions(normalizedRfpId);
      const response = await heartbeatRfpPresence(normalizedRfpId);
      if (response.sync_pipeline_running === true) {
        applyHeartbeatResponse(response);
      } else {
        setActiveUsers(response.active_users ?? []);
        prevPresenceSyncRunningRef.current = false;
        setPresenceSyncRunning(false);
        refreshRfp();
        setIsSyncing(false);
      }
    } catch (e) {
      setIsSyncing(false);
      const message = e instanceof Error ? e.message : "Failed to start sync";
      setSyncError(message);
    }
  }, [applyHeartbeatResponse, isSyncRunning, normalizedRfpId, syncEligibleCount]);
  const handleCloseAssignDrawer = useCallback(() => {
    if (isAssigning) {
      return;
    }
    setIsAssignDrawerOpen(false);
  }, [isAssigning]);
  const handleOpenFindAnswerDrawer = useCallback((context: RfpQuestionFindAnswerContext) => {
    setFindAnswerContext(context);
    setIsFindAnswerDrawerOpen(true);
  }, []);
  const handleCloseFindAnswerDrawer = useCallback(() => {
    setIsFindAnswerDrawerOpen(false);
    setFindAnswerContext(null);
  }, []);
  const handleFindAnswerApplySuccess = useCallback(() => {
    handleCloseFindAnswerDrawer();
    refreshRfp();
  }, [handleCloseFindAnswerDrawer, refreshRfp]);
  const handleSelectAnswer = useCallback(
    async ({ questionId, selectedAnswer, addToKnowledgeBase }: RfpQuestionSelectAnswerContext) => {
      if (!normalizedRfpId) return;

      setSelectError(null);
      setSelectingQuestionId(questionId);
      try {
        await selectRfpQuestionAnswer(normalizedRfpId, questionId, {
          selected_answer: selectedAnswer,
        });
        if (addToKnowledgeBase) {
          await addRfpQuestionToKb(normalizedRfpId, questionId);
        }
        refreshRfp();
      } catch (e) {
        const message = e instanceof Error ? e.message : "Failed to select answer";
        setSelectError(message);
      } finally {
        setSelectingQuestionId((current) => (current === questionId ? null : current));
      }
    },
    [normalizedRfpId, refreshRfp],
  );
  const handleDeselectAnswer = useCallback(
    async ({ questionId }: RfpQuestionDeselectAnswerContext) => {
      if (!normalizedRfpId) return;

      setDeselectError(null);
      setDeselectingQuestionId(questionId);
      try {
        await deselectRfpQuestionAnswer(normalizedRfpId, questionId);
        refreshRfp();
      } catch (e) {
        const message = e instanceof Error ? e.message : "Failed to deselect answer";
        setDeselectError(message);
      } finally {
        setDeselectingQuestionId((current) => (current === questionId ? null : current));
      }
    },
    [normalizedRfpId, refreshRfp],
  );
  const handleLockQuestion = useCallback(
    async ({ questionId }: RfpQuestionLockContext) => {
      if (!normalizedRfpId) return;

      setLockError(null);
      setLockingQuestionId(questionId);
      try {
        await lockRfpQuestion(normalizedRfpId, questionId);
        refreshRfp();
      } catch (e) {
        const message = e instanceof Error ? e.message : "Failed to lock question";
        setLockError(message);
      } finally {
        setLockingQuestionId((current) => (current === questionId ? null : current));
      }
    },
    [normalizedRfpId, refreshRfp],
  );
  const handleUnlockQuestion = useCallback(
    async ({ questionId }: RfpQuestionLockContext) => {
      if (!normalizedRfpId) return;

      setUnlockError(null);
      setUnlockingQuestionId(questionId);
      try {
        await unlockRfpQuestion(normalizedRfpId, questionId);
        refreshRfp();
      } catch (e) {
        const message = e instanceof Error ? e.message : "Failed to unlock question";
        setUnlockError(message);
      } finally {
        setUnlockingQuestionId((current) => (current === questionId ? null : current));
      }
    },
    [normalizedRfpId, refreshRfp],
  );
  const handleUpdateAnswer = useCallback(
    async ({ questionId, payload }: RfpQuestionUpdateAnswerContext) => {
      if (!normalizedRfpId) return;

      setUpdateError(null);
      setUpdatingQuestionId(questionId);
      try {
        await updateRfpQuestionAnswer(normalizedRfpId, questionId, payload);
        refreshRfp();
      } catch (e) {
        const message = e instanceof Error ? e.message : "Failed to update answer";
        setUpdateError(message);
        throw e;
      } finally {
        setUpdatingQuestionId((current) => (current === questionId ? null : current));
      }
    },
    [normalizedRfpId, refreshRfp],
  );
  const handleExport = useCallback(async () => {
    if (!normalizedRfpId) return;

    setExportError(null);
    setIsExporting(true);
    try {
      const { blob, fileName } = await exportRfpFile(normalizedRfpId);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to export RFP";
      setExportError(message);
    } finally {
      setIsExporting(false);
    }
  }, [normalizedRfpId]);
  const handleAssignQuestions = useCallback(
    async (payload: BulkAssignRfpQuestionsRequest): Promise<boolean> => {
      if (!normalizedRfpId || isAssigning) {
        return false;
      }

      setAssignError(null);
      setIsAssigning(true);
      try {
        await bulkAssignRfpQuestions(normalizedRfpId, payload);
        setIsAssignDrawerOpen(false);
        refreshRfp();
        return true;
      } catch (e) {
        const message = e instanceof Error ? e.message : "Failed to assign questions";
        setAssignError(message);
        return false;
      } finally {
        setIsAssigning(false);
      }
    },
    [isAssigning, normalizedRfpId, refreshRfp],
  );
  const allSections = orderedSections;
  const assignSections: AssignSectionOption[] = useMemo(
    () => allSections.map((section) => ({ name: section.name, total: section.total })),
    [allSections],
  );
  const assignQuestions: AssignQuestionOption[] = useMemo(() => {
    let questionNumber = 0;
    return allSections.flatMap((section) =>
      section.questions.map((question) => {
        questionNumber += 1;
        return {
          id: question.id,
          indexLabel: `Q${questionNumber}`,
          sectionName: section.name,
          text: question.better_question || question.raw_question,
        };
      }),
    );
  }, [allSections]);
  const availableUsers = useMemo<readonly RfpAssignableUser[]>(
    () =>
      rfpUsers
        .filter((user) => user.is_active)
        .map((user) => ({ id: user.id, name: user.name, email: user.email })),
    [rfpUsers],
  );

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    if (!normalizedRfpId) {
      setActiveUsers([]);
      setPresenceSyncRunning(false);
      prevPresenceSyncRunningRef.current = false;
      return;
    }

    let isCancelled = false;
    const sendHeartbeat = async () => {
      try {
        const response = await heartbeatRfpPresence(normalizedRfpId);
        if (!isCancelled) {
          applyHeartbeatResponse(response);
        }
      } catch {
        // Keep the page stable even if presence is temporarily unavailable.
      }
    };

    void sendHeartbeat();
    const intervalId = window.setInterval(() => {
      void sendHeartbeat();
    }, PRESENCE_HEARTBEAT_INTERVAL_MS);

    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
    };
  }, [applyHeartbeatResponse, normalizedRfpId]);

  if (!normalizedRfpId) {
    return (
      <section className="page-section">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
          Missing RFP id in URL. Open this page from the My RFPs action button.
        </div>
        <div>
          <button
            type="button"
            onClick={() => navigate("/my-rfps")}
            className="rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-white"
          >
            Back to My RFPs
          </button>
        </div>
      </section>
    );
  }

  if (error && !rfp) {
    return (
      <section className="page-section">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => reload()}
            className="rounded-md bg-white px-4 py-2 text-sm font-medium text-red-700 ring-1 ring-red-200"
          >
            Retry
          </button>
          <button
            type="button"
            onClick={() => navigate("/my-rfps")}
            className="rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-white"
          >
            Back to My RFPs
          </button>
        </div>
      </section>
    );
  }

  if (!loading && !rfp) {
    return (
      <section className="page-section">
        <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
          RFP details were not found.
        </div>
      </section>
    );
  }

  const hasRfpContent = !!rfp;
  const showQuestionList = !!activeSection;
  const visibleStart = hasRfpContent && filteredQuestions.length ? pageStart + 1 : 0;
  const visibleEnd =
    hasRfpContent && filteredQuestions.length
      ? Math.min(pageStart + QUESTIONS_PER_PAGE, filteredQuestions.length)
      : 0;
  const footerSummary =
    hasRfpContent && activeSection
      ? assignedToMeOnly
        ? `Viewing ${visibleStart}-${visibleEnd} of ${filteredQuestions.length} questions assigned to you in ${activeSection.name}`
        : `Viewing ${visibleStart}-${visibleEnd} of ${filteredQuestions.length} questions in ${activeSection.name}`
      : assignedToMeOnly && hasRfpContent && assignedToMeCount === 0
        ? "No questions are assigned to you in this RFP."
        : "Loading RFP questions...";
  const rfpProgress =
    hasRfpContent && rfp ? rfp.progress : { completed: 0, total: 0, percentage: 0 };
  const sidebarProgress =
    assignedToMeOnly && hasRfpContent
      ? {
          completed: myAssignmentProgress.completed,
          total: myAssignmentProgress.total,
          percentage: myAssignmentProgress.percentage,
        }
      : rfpProgress;
  const sidebarProgressScopeLabel = assignedToMeOnly ? "answered (yours)" : "answered";
  const ownerDisplay = hasRfpContent && rfp ? formatRfpUserDisplay(rfp.owner) : "—";
  const uploadedByDisplay = hasRfpContent && rfp ? formatRfpUserDisplay(rfp.created_by) : "—";
  const discussionContextLabel = discussionContext
    ? `${discussionContext.indexLabel} - ${discussionContext.answerLabel}`
    : "Question - Answer";
  const showPageOverlayLoader = loading;

  return (
    <section className="-mx-6 -my-8 relative flex h-[calc(100vh-70px)] flex-col bg-gray-50">
      {hasRfpContent && rfp && (
        <>
          <RfpQuestionAnswersHeader
            title={rfp.name}
            status={rfp.status}
            dueDate={rfp.due_date}
            ownerName={ownerDisplay}
            uploadedByName={uploadedByDisplay}
            searchInput={searchInput}
            onSearchChange={setSearchInput}
            assignedToMeOnly={assignedToMeOnly}
            onAssignedToMeOnlyChange={setAssignedToMeOnly}
            onBack={() => navigate("/my-rfps")}
            onAddQuestion={handleOpenAddQuestionDrawer}
            onSyncPending={() => void handleSyncPending()}
            syncEligibleCount={syncEligibleCount}
            isSyncing={isSyncRunning}
            onAssignQuestions={handleOpenAssignDrawer}
            onExport={handleExport}
            isExporting={isExporting}
            activeUsers={activeUsers}
          />

          <div className="flex min-h-0 flex-1">
            <RfpQuestionAnswerSectionSidebar
              sections={sidebarSections}
              activeSectionName={activeSectionName}
              progress={sidebarProgress}
              progressScopeLabel={sidebarProgressScopeLabel}
              onSectionChange={handleSectionChange}
            />

            <div className="min-h-0 flex-1 overflow-auto px-6 py-5">
              <div className="mx-auto max-w-5xl space-y-4">
                {exportError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    {exportError}
                  </div>
                )}
                {selectError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    {selectError}
                  </div>
                )}
                {deselectError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    {deselectError}
                  </div>
                )}
                {lockError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    {lockError}
                  </div>
                )}
                {unlockError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    {unlockError}
                  </div>
                )}
                {updateError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    {updateError}
                  </div>
                )}
                {syncError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    {syncError}
                  </div>
                )}
                {!showQuestionList ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
                    No questions are assigned to you in this RFP. Turn off &quot;Assigned to
                    me&quot; to view all questions.
                  </div>
                ) : pagedQuestions.length ? (
                  pagedQuestions.map((question, index) => (
                    <RfpQuestionAnswerCard
                      key={question.id}
                      question={question}
                      indexLabel={`Q${pageStart + index + 1}`}
                      onDiscuss={handleOpenDiscussion}
                      onFindAnswer={handleOpenFindAnswerDrawer}
                      onSelectAnswer={handleSelectAnswer}
                      onDeselectAnswer={handleDeselectAnswer}
                      onLockQuestion={handleLockQuestion}
                      onUnlockQuestion={handleUnlockQuestion}
                      onUpdateAnswer={handleUpdateAnswer}
                      isSelecting={selectingQuestionId === question.id}
                      isDeselecting={deselectingQuestionId === question.id}
                      isLocking={lockingQuestionId === question.id}
                      isUnlocking={unlockingQuestionId === question.id}
                      isUpdating={updatingQuestionId === question.id}
                    />
                  ))
                ) : (
                  <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-600">
                    {assignedToMeOnly
                      ? "No questions assigned to you match your search in this section."
                      : "No questions found for the selected section."}
                  </div>
                )}

                {showQuestionList && filteredQuestions.length > 0 && (
                  <div className="flex items-center justify-center gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => setPage((value) => Math.max(1, value - 1))}
                      disabled={currentPage === 1}
                      className="inline-flex items-center gap-1 rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                      <button
                        key={pageNumber}
                        type="button"
                        onClick={() => setPage(pageNumber)}
                        className={`rounded border px-3 py-1.5 text-sm ${
                          pageNumber === currentPage
                            ? "border-primary-500 bg-primary-500 text-white"
                            : "border-gray-300 text-gray-700"
                        }`}
                      >
                        {pageNumber}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                      disabled={currentPage === totalPages}
                      className="inline-flex items-center gap-1 rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {isSyncRunning ? (
            <div
              className="pointer-events-none fixed bottom-6 right-6 z-20 flex max-w-sm items-start gap-2 rounded-lg border border-sky-200 bg-white px-4 py-3 text-sm text-sky-900 shadow-lg"
              role="status"
              aria-live="polite"
            >
              <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-primary-500" />
              <span>
                Generating answers in the background. You can keep working — answers refresh
                when sync completes.
              </span>
            </div>
          ) : null}

          <RfpQuestionAnswersFooter summaryText={footerSummary} />
        </>
      )}

      {showPageOverlayLoader && (
        <div
          className="absolute inset-0 z-40 flex items-center justify-center bg-white/60 backdrop-blur-[1px]"
          role="status"
          aria-live="polite"
        >
          <div className="inline-flex items-center gap-2 rounded-md bg-white/90 px-3 py-2 text-sm text-gray-700 shadow-sm">
            <svg
              className="h-4 w-4 animate-spin text-primary-500"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            Loading RFP questions...
          </div>
        </div>
      )}

      {hasRfpContent && (
        <>
          <RfpDiscussionDrawer
            isOpen={isDiscussionOpen}
            onClose={handleCloseDiscussion}
            rfpId={normalizedRfpId}
            questionId={discussionContext?.questionId ?? null}
            contextLabel={discussionContextLabel}
            users={availableUsers}
          />
          <RfpAssignQuestionsDrawer
            isOpen={isAssignDrawerOpen}
            onClose={handleCloseAssignDrawer}
            sections={assignSections}
            questions={assignQuestions}
            users={availableUsers}
            isSubmitting={isAssigning}
            errorMessage={assignError}
            onAssign={handleAssignQuestions}
          />
          <RfpFindAnswerDrawer
            isOpen={isFindAnswerDrawerOpen}
            onClose={handleCloseFindAnswerDrawer}
            rfpId={normalizedRfpId}
            questionId={findAnswerContext?.questionId ?? null}
            questionText={findAnswerContext?.questionText ?? "No question selected."}
            onApplySuccess={handleFindAnswerApplySuccess}
          />
          <RfpAddQuestionDrawer
            isOpen={isAddQuestionOpen}
            onClose={handleCloseAddQuestionDrawer}
            defaultSectionName={defaultAddQuestionSection}
            sectionOptions={sectionNameOptions}
            isSubmitting={isAddingQuestion}
            errorMessage={addQuestionError}
            onSubmit={handleAddQuestion}
          />
        </>
      )}
    </section>
  );
};
